import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import QueryProvider from '@jwp/ott-ui-react/src/containers/QueryProvider/QueryProvider';
import { ErrorPageWithoutTranslation } from '@jwp/ott-ui-react/src/components/ErrorPage/ErrorPage';
import LoadingOverlay from '@jwp/ott-ui-react/src/components/LoadingOverlay/LoadingOverlay';
import { AriaAnnouncerProvider } from '@jwp/ott-ui-react/src/containers/AnnouncementProvider/AnnoucementProvider';
import { CookiesConfirmation } from '@jwp/ott-ui-react/src/components/Cookies/Cookies';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
// import { getAnalytics } from 'firebase/analytics';
// import { app } from './firebase';
import { captureUTMs } from '@jwp/ott-common/src/utils/utmTracker';

import initI18n from './i18n/config';
import Root from './containers/Root/Root';
import './registerCustomScreens';
import './styles/main.scss';
import env from '@jwp/ott-common/src/env';

interface State {
  isLoading: boolean;
  error?: Error;
}

export default function App() {
  const [i18nState, seti18nState] = useState<State>({ isLoading: true });
  const { appConfig, analyticsConfig } = useConfigStore.getState();

  const { isUtmEnabled, isFirebaseAnalytics, isAdobeAnalytics, adobeScripTag, isGoogleCaptchaEnabled, authentication_type } = useAppConfig();
  const { canShowConsentManagement } = useUIControls();
  const analytics = analyticsConfig;

  const mobileRedirect = () => {
    //  const ua = navigator.userAgent || navigator.vendor;

    // const isIOS = /iPad|iPhone|iPod/.test(ua);
    // const isAndroid = /android/i.test(ua);
    // const currentUrl = encodeURIComponent(window.location.href);
    // if (isIOS) {
    //   window.location.replace(
    //     `${env.APP_IOS_APP_STORE_URL}`,
    //   );
    // } else if (isAndroid) {
    //   window.location.replace(
    //     ` ${env.APP_ANDROID_PLAY_STORE_URL}&referrer=${currentUrl}`,
    //   );
    // }


    const ua = navigator.userAgent;
    const isTouchDevice = navigator.maxTouchPoints > 0;
    const looksLikeDesktop = !/iPhone|iPad|iPod|Android/i.test(ua);

    const isIOS = /Mac/i.test(ua) && isTouchDevice;
    const isAndroid = /Linux/i.test(ua) && isTouchDevice;

    if (isIOS) {
      window.location.replace(
        `${env.APP_IOS_APP_STORE_URL}`,
      );
    } else if (isAndroid) {
      window.location.replace(
        `${env.APP_ANDROID_PLAY_STORE_URL}`,
      );
    }
  }

  useEffect(() => {
    mobileRedirect();
  }, []);

  //useEffects
  useEffect(() => {
    if (appConfig?.branding?.favicon_url) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      // let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;

      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }

      link.href = appConfig?.branding?.favicon_url;
    }
  }, [appConfig?.branding?.favicon_url]);

  useEffect(() => {
    if (isAdobeAnalytics && adobeScripTag) {
      const scriptId = 'adobe-launch-script';
      const existingAdobeScript = document.getElementById(scriptId);
      if (!existingAdobeScript) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = adobeScripTag;
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, []);



  // useEffect(() => {
  //   if (isFirebaseAnalytics) {
  //     getAnalytics(app);
  //   }
  // }, []);

  useEffect(() => {
    if (import.meta.env.APP_CLIENT_NAME) {
      useConfigStore.setState((prevState) => ({
        ...prevState,
        config: {
          ...prevState.config,
          siteName: import.meta.env.APP_CLIENT_NAME,
        },
      }));
    }
  }, []);

  useEffect(() => {
    initI18n()
      .then(() => seti18nState({ isLoading: false }))
      .catch((e) => seti18nState({ isLoading: false, error: e as Error }));
  }, []);

  useEffect(() => {
    window.cleeng?.onAuthTokensUpdate?.(({ jwt, refreshToken }) => {
      localStorage.setItem('jwapp.auth', JSON.stringify({ accessToken: jwt, refreshToken }));
    });
  }, []);

  useEffect(() => {
    if (analytics) {
      window.analyticsConfig = analytics;
    }
  }, [analytics]);

  useEffect(() => {
    if (isUtmEnabled) {
      captureUTMs();
    }
  }, []);

  if (i18nState.isLoading) {
    return <LoadingOverlay />;
  }

  if (i18nState.error) {
    // Don't be tempted to translate these strings. If i18n fails to load, translations won't work anyhow
    return (
      <ErrorPageWithoutTranslation
        title={'Unable to load translations'}
        message={'Check your language settings and try again later. If the problem persists contact technical support.'}
        error={i18nState.error}
      />
    );
  }

  const App = () => (
    <>
      {canShowConsentManagement && <CookiesConfirmation />}
      <QueryProvider>
        <BrowserRouter>
          <AriaAnnouncerProvider>
            <Root />
          </AriaAnnouncerProvider>
        </BrowserRouter>
      </QueryProvider>
    </>
  );

  return (
    <>
      {isGoogleCaptchaEnabled ? (
        <GoogleReCaptchaProvider
          reCaptchaKey={import.meta.env.APP_RECAPTCHA_SITE_KEY}
          scriptProps={{
            async: false,
            defer: false,
            appendTo: 'head',
            nonce: undefined,
          }}
        >
          <App />
        </GoogleReCaptchaProvider>
      ) : (
        <App />
      )}
    </>
  );
}
